import 'extensions/ui-components/x-date-input';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-quantity';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-multi-system-basket-container.scss?as=stylesheet';

export class MultiSystemBasketContainer extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			baseSubtotal: {
				attribute: 'base-subtotal',
				type: Number
			},
			deliveryDate: {
				attribute: 'delivery-date',
				type: String
			},
			formattedBaseSubtotal: {
				attribute: 'formatted-base-subtotal',
				type: String
			},
			formattedDeliveryDate: {
				attribute: 'formatted-delivery-date',
				type: String
			},
			formattedSubtotal: {
				attribute: 'formatted-subtotal',
				type: String
			},
			hidePricing: {
				attribute: 'hide-pricing',
				type: Boolean
			},
			id: {
				type: String
			},
			loading: {
				type: Boolean
			},
			quantity: {
				type: Number
			},
			subtotal: {
				type: Number
			},
			updateUrl: {
				attribute: 'update-url',
				type: String
			},
			wishlistUrl: {
				attribute: 'wishlist-url',
				type: String
			}
		};
	}

	// eslint-disable-next-line max-lines-per-function
	constructor () {
		super();

		/**
		 * @type {number}
		 */
		this.baseSubtotal = 0;

		/**
		 * @type {string}
		 */
		this.deliveryDate = '';

		/**
		 * @type {string}
		 */
		this.formattedBaseSubtotal = '';

		/**
		 * @type {string}
		 */
		this.formattedDeliveryDate = '';

		/**
		 * @type {string}
		 */
		this.formattedSubtotal = '';

		/**
		 * @type {boolean}
		 */
		this.hidePricing = false;

		/**
		 * @type {string}
		 */
		this.id = '';

		/**
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * @type {number}
		 */
		this.quantity = 0;

		/**
		 * @type {number}
		 */
		this.subtotal = 0;

		/**
		 * @type {string}
		 */
		this.updateUrl = '';

		/**
		 * @type {string}
		 */
		this.wishlistUrl = '';
	}

	// eslint-disable-next-line max-lines-per-function
	render () {
		const classes = {
			container: true,
			loading: this.loading
		};

		return html`
			<div class="${classMap(classes)}">
				${this.loading ? html`<x-loader class="loader" aspect-ratio-desktop="1600/1200" aspect-ratio-tablet="820/1100" aspect-ratio="390/740"></x-loader>` : nothing}
				<div class="heading">
					<div class="title">Multi System</div>
					<div class="heading-right">
						<div class="actions">
							<form action="${this.updateUrl}" method="POST" class="delivery-date-form" @submit="${this.setLoading}">
								<input type="hidden" name="Action" value="MULTISYSTEM_DELIVERY_DATE" />
								<input type="hidden" name="MultiSystemGroup" value="${this.id}" />
								<input type="hidden" name="MultiSystemDate" value="${this.deliveryDate}" />
								<x-date-input disable-weekends name="MultiSystemDatePicker" value="${this.dateTimeFormat}" @change="${this.handleDateChange}"></x-date-input>
							</form>
							<form action="${this.updateUrl}" method="POST" class="quantity-form" @submit="${this.setLoading}">
								<input type="hidden" name="Action" value="MULTISYSTEM_QUANTITY_UPDATE" />
								<input type="hidden" name="MultiSystemGroup" value="${this.id}" />
								<x-quantity class="quantity" name="Quantity" value="${this.quantity}" hide-buttons-mobile hide-buttons-tablet @change="${this.handleQuantityChange}"></x-quantity>
							</form>
							<form action="${this.updateUrl}" method="POST" class="remove-form" @submit="${this.setLoading}">
								<input type="hidden" name="Action" value="MULTISYSTEM_QUANTITY_UPDATE" />
								<input type="hidden" name="MultiSystemGroup" value="${this.id}" />
								<input type="hidden" name="Quantity" value="0" />
								<button type="submit" class="remove-form-button" aria-label="Remove" @click="${this.handleRemoveButtonClick}"></button>
							</form>
						</div>
						<div class="subtotal">
							<div class="subtotal-label">Subtotal</div>
							<div class="subtotal-value">${this.hidePricing ? html`<span class="hide-pricing"></span>` : this.formattedSubtotal}</div>
						</div>
					</div>
				</div>
				<div class="items">
					<slot></slot>
				</div>
				<form action="${this.wishlistUrl}" method="POST" class="wishlist-form" @submit="${this.setLoading}">
					<input type="hidden" name="Action" value="MULTISYSTEM_WISHLIST" />
					<input type="hidden" name="MultiSystemGroup" value="${this.id}" />
					<input type="hidden" name="Wishlist_Title" value="Multi System" />
					<button type="submit" class="wishlist-button">Move To Saved List</button>
				</form>
			</div>
		`;
	}

	async handleDateChange (e) {
		const dateElement = e.target;
		let newDate = '';
		if (dateElement?.value !== '') {
			const date = new Date(dateElement?.value);
			newDate = date.toISOString().substring(0, 19);
		}

		if (this.deliveryDate === newDate) {
			return;
		}

		this.deliveryDate = newDate;

		await this.updateComplete;

		dateElement?.form?.requestSubmit();
	}

	handleQuantityChange (e) {
		const [target] = e.composedPath();

		target.form?.requestSubmit();
	}

	handleRemoveButtonClick (e) {
		// eslint-disable-next-line no-alert
		const dialog = window?.confirm('Are you sure you want to remove the Multi System from your cart?');

		if (!dialog) {
			e?.preventDefault();
			return;
		}

		// Trigger `remove_from_cart Event for GA.
		const children = Array.from(this.children)?.filter(child => child.nodeName === 'X-MULTI-BASKET-ITEM');
		const gtmItems = [];
		let gtmValue = 0;

		for (const child of children) {
			gtmItems.push({
				/* eslint-disable camelcase */
				item_id: child.code,
				item_name: child.name,
				/* eslint-enable camelcase */
				price: child.price,
				quantity: child.quantity
			});

			gtmValue += child.subtotal;
		}

		if (gtmItems.length) {
			window.dataLayer.push({
				ecommerce: {
					items: gtmItems,
					value: parseFloat(gtmValue.toFixed(2))
				},
				event: 'remove_from_cart'
			});
		}
	}

	setLoading () {
		this.loading = true;
	}

	get dateTimeFormat () {
		if (!this.deliveryDate) {
			return '';
		}

		const d = new Date(this.deliveryDate);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}
};

defineCustomElement('x-multi-basket-container', MultiSystemBasketContainer);
